// client/src/revenuecat.js
// Капсулира цялата RevenueCat логика. Работи на iOS и Android native (не на web).
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';

const RC_IOS_API_KEY = 'appl_YkXpEWFOOSFtvWcOgaxnYkZEJJp'; // production Apple ключ
const RC_ANDROID_API_KEY = 'goog_QTPdWvjsXKlrBYArGeMOtvspddj'; // production Google Play ключ
const ENTITLEMENT_ID = 'Pro'; // case-sensitive, от RevenueCat dashboard

let configured = false;

export function isIOS() {
  return Capacitor.getPlatform() === 'ios';
}

export function isAndroid() {
  return Capacitor.getPlatform() === 'android';
}

// true на iOS или Android (т.е. навсякъде, където RevenueCat работи). На web връща false.
export function isNative() {
  return isIOS() || isAndroid();
}

// Връща правилния API ключ за текущата платформа
function getApiKey() {
  if (isIOS()) return RC_IOS_API_KEY;
  if (isAndroid()) return RC_ANDROID_API_KEY;
  return null;
}

// Извиква се веднъж при старт на приложението (iOS или Android)
export async function initRevenueCat() {
  if (!isNative() || configured) return;
  const apiKey = getApiKey();
  if (!apiKey) return;
  try {
    await Purchases.configure({ apiKey });
    configured = true;
    console.log('[RC] Configured for', Capacitor.getPlatform());
  } catch (e) {
    console.error('[RC] Configure error:', e);
  }
}

// Свързва RevenueCat customer-а с Firebase UID.
// КРИТИЧНО: този UID отива в webhook-а като app_user_id.
export async function rcLogin(firebaseUID) {
  if (!isNative() || !firebaseUID) return;
  try {
    if (!configured) await initRevenueCat();
    await Purchases.logIn({ appUserID: firebaseUID });
    console.log('[RC] Logged in:', firebaseUID);
  } catch (e) {
    console.error('[RC] Login error:', e);
  }
}

export async function rcLogout() {
  if (!isNative() || !configured) return;
  try {
    await Purchases.logOut();
    console.log('[RC] Logged out');
  } catch (e) {
    console.error('[RC] Logout error:', e);
  }
}

// Връща current offering-а (packages) за paywall-а
export async function getOfferings() {
  if (!isNative()) return null;
  try {
    if (!configured) await initRevenueCat();
    const result = await Purchases.getOfferings();
    console.log('[RC] offerings keys:', result ? Object.keys(result) : 'null');
    return result?.current || null;
  } catch (e) {
    console.error('[RC] getOfferings error:', e);
    return null;
  }
}

// Купува package. Връща true ако потребителят сега има Pro entitlement.
// КРИТИЧНО: преди покупка гарантираме, че RevenueCat е логнат с Firebase UID,
// за да НЕ тръгне покупката под анонимен ID (иначе webhook-ът праща грешен
// app_user_id и backend-ът не може да активира Pro за правилния потребител).
export async function purchasePackage(pkg, firebaseUID) {
  if (!configured) await initRevenueCat();
  // Ако имаме UID — уверяваме се, че текущият RC customer е точно той.
  if (firebaseUID) {
    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      const currentId = customerInfo?.originalAppUserId || '';
      // Логваме само ако още не сме този потребител (или сме анонимни)
      if (currentId !== firebaseUID || currentId.startsWith('$RCAnonymousID')) {
        await Purchases.logIn({ appUserID: firebaseUID });
        console.log('[RC] Ensured login before purchase:', firebaseUID);
      }
    } catch (e) {
      // Ако getCustomerInfo гръмне, пак опитваме login за всеки случай
      try { await Purchases.logIn({ appUserID: firebaseUID }); } catch (_) {}
    }
  }
  const result = await Purchases.purchasePackage({ aPackage: pkg });
  const info = result?.customerInfo;
  return !!info?.entitlements?.active?.[ENTITLEMENT_ID];
}

// Възстановяване на покупки (Apple изисква бутон за това; на Android също е полезно)
export async function restorePurchases() {
  const result = await Purchases.restorePurchases();
  const info = result?.customerInfo;
  return !!info?.entitlements?.active?.[ENTITLEMENT_ID];
}

// Проверка дали потребителят има активен Pro entitlement
export async function checkProEntitlement() {
  if (!isNative()) return false;
  try {
    if (!configured) await initRevenueCat();
    const result = await Purchases.getCustomerInfo();
    const info = result?.customerInfo;
    return !!info?.entitlements?.active?.[ENTITLEMENT_ID];
  } catch (e) {
    console.error('[RC] checkProEntitlement error:', e);
    return false;
  }
}

// Връща детайли за активния Pro abonament (от кога/до кога)
export async function getProSubscriptionInfo() {
  if (!isNative()) return null;
  try {
    if (!configured) await initRevenueCat();
    const result = await Purchases.getCustomerInfo();
    const ent = result?.customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
    if (!ent) return null;
    return {
      isActive: true,
      productId: ent.productIdentifier,
      latestPurchaseDate: ent.latestPurchaseDate,
      expirationDate: ent.expirationDate,
      willRenew: ent.willRenew,
      periodType: ent.periodType,
    };
  } catch (e) {
    console.error('[RC] getProSubscriptionInfo error:', e);
    return null;
  }
}
